import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import type { ComparisonReportListItem, ComparisonReportListResponse } from '../../types';
import type { DimensionFilter } from '../../../types/filters';

const PAGE_SIZE = 50;

interface ComparisonReportSearchPayload {
    filters: Record<string, DimensionFilter>;
    search_key: string;
    my_reports: boolean;
    sort_by: string;
    sorting_order: 'asc' | 'desc';
    limit: number;
    offset: number;
}

const fetchComparisonReports = async (
    searchKey: string,
    myReports: boolean,
    filters: Record<string, DimensionFilter>,
    sortBy: string,
    sortOrder: 'asc' | 'desc',
    offset: number,
    signal?: AbortSignal,
): Promise<ComparisonReportListResponse> => {
    let token = '';
    try {
        token = localStorage.getItem('accessToken') ?? '';
    } catch {}
    const payload: ComparisonReportSearchPayload = {
        filters,
        search_key: searchKey,
        my_reports: myReports,
        sort_by: sortBy,
        sorting_order: sortOrder,
        limit: PAGE_SIZE,
        offset,
    };
    const response = await axios(tpoGenUrls.getComparisonReports, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        data: payload,
        signal,
    });
    return response.data;
};

interface UseComparisonReportListArgs {
    searchKey: string;
    myReports: boolean;
    filters: Record<string, DimensionFilter>;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
}

interface UseComparisonReportListResult {
    data: ComparisonReportListItem[];
    isPending: boolean;
    isFetching: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
}

const useComparisonReportList = ({
    searchKey,
    myReports,
    filters,
    sortBy,
    sortOrder,
}: UseComparisonReportListArgs): UseComparisonReportListResult => {
    const { data, isPending, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: tpoGenKeys.comparisonReportList(searchKey, myReports, filters, sortBy, sortOrder),
        queryFn: ({ signal, pageParam }) =>
            fetchComparisonReports(searchKey, myReports, filters, sortBy, sortOrder, pageParam, signal),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const reports = lastPage?.data ?? [];
            return reports.length === PAGE_SIZE ? lastPageParam + PAGE_SIZE : undefined;
        },
    });

    const reports = useMemo(() => data?.pages.flatMap((page) => page?.data ?? []) ?? [], [data]);

    return { data: reports, isPending, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage };
};

export default useComparisonReportList;
